import { formatISO } from 'date-fns';
import { cast, types } from 'mobx-state-tree';
import moment from 'moment';
import { api } from 'src/api';
import { toJS } from 'mobx';
import { createAsync } from 'src/utils/asyncModel';
import { NOTIFICATION_DEFAULT } from 'src/constants/notifications';
const getAllNotificationsAsync = createAsync(() => {
  return async function getAllNotifications(options) {
    const resp = await api.Notifications.getAllNotifications(options);
    this.setNotificationList(resp.response);
    return resp;
  };
});
const createNewNotificationAsync = createAsync(body => {
  return async function createNewNotification(options) {
    const resp = await api.Notifications.createNewNotification(body, options);
    return resp;
  };
});
const getSpecificNotificationsAsync = createAsync(body => {
  return async function getSpecificNotifications(options) {
    const resp = await api.Notifications.getSpecificNotifications(body, options);
    this.setNewNotification(resp.response);
    return resp;
  };
});
const putSpecificNotificationsAsync = createAsync(body => {
  return async function putSpecificNotifications(options) {
    const resp = await api.Notifications.putSpecificNotifications(body, options);
    return resp;
  };
});
const deleteSpecificNotificationsAsync = createAsync(body => {
  return async function deleteSpecificNotifications(options) {
    const resp = await api.Notifications.deleteSpecificNotifications(body, options);
    return resp;
  };
});
const publishNotificationAsync = createAsync(body => {
  return async function publishNotification(options) {
    const resp = await api.Notifications.publishNotification(body, options);
    return resp;
  };
});
const unpublishNotificationAsync = createAsync(body => {
  return async function unpublishNotification(options) {
    const resp = await api.Notifications.unpublishNotification(body, options);
    return resp;
  };
});
const getPendingNotificationsAsync = createAsync(() => {
  return async function getPendingNotifications(options) {
    const resp = await api.Notifications.getPendingNotifications(options);
    this.resetCurrentToS();
    const tosIdx = resp.response.findIndex(item => item.appearanceType === 'TermsOfServicesModal');
    if (tosIdx >= 0) {
      const tosData = {
        id: resp.response[tosIdx].id,
        title: resp.response[tosIdx].title,
        body: resp.response[tosIdx].body,
        termsOfConditions: !!resp.response[tosIdx].termsOfConditions ? true : false,
        privacyPolicy: !!resp.response[tosIdx].privacyPolicy ? true : false,
        userAcknowledgement: !!resp.response[tosIdx].userAcknowledgement ? true : false
      };
      this.setCurrentToS(tosData);
    }
    return resp;
  };
});
const viewNotificationAsync = createAsync(body => {
  return async function viewNotification(options) {
    const resp = await api.Notifications.viewNotification(body, options);
    return resp;
  };
});
const acknowledgementNotificationAsync = createAsync(body => {
  return async function acknowledgementNotification(options) {
    const resp = await api.Notifications.acknowledgementNotification(body, options);
    return resp;
  };
});
const Notification = types.model({
  id: types.string,
  title: types.maybeNull(types.string),
  body: types.maybeNull(types.string),
  startDateOnly: types.maybeNull(types.string),
  endDateOnly: types.maybeNull(types.string),
  showButton: types.boolean,
  buttonTitle: types.maybeNull(types.string),
  buttonUrl: types.maybeNull(types.string),
  notificationType: types.maybeNull(types.union(types.number, types.string)),
  appearanceType: types.maybeNull(types.union(types.number, types.string)),
  termsOfConditions: types.maybeNull(types.string),
  privacyPolicy: types.maybeNull(types.string),
  userAcknowledgement: types.maybeNull(types.string),
  isPublished: types.boolean
});
const NewNotification = types.model({
  appearanceType: types.maybeNull(types.union(types.number, types.string)),
  type: types.string,
  title: types.string,
  body: types.string,
  button: types.string,
  buttonTitle: types.maybeNull(types.string),
  buttonLink: types.maybeNull(types.string),
  dates: types.string,
  startDateOnly: types.string,
  endDateOnly: types.maybeNull(types.string),
  termsOfConditions: types.maybeNull(types.boolean),
  privacyPolicy: types.maybeNull(types.boolean),
  userAcknowledgement: types.maybeNull(types.boolean)
});
const TosNotification = types.model({
  id: types.string,
  title: types.string,
  body: types.string,
  termsOfConditions: types.maybeNull(types.boolean),
  privacyPolicy: types.maybeNull(types.boolean),
  userAcknowledgement: types.maybeNull(types.boolean)
});
const NotificationsStore = types.model('NotificationsStore', {
  viewNotificationAsync,
  getAllNotificationsAsync,
  publishNotificationAsync,
  createNewNotificationAsync,
  unpublishNotificationAsync,
  getPendingNotificationsAsync,
  getSpecificNotificationsAsync,
  putSpecificNotificationsAsync,
  deleteSpecificNotificationsAsync,
  acknowledgementNotificationAsync,
  notificationListValue: types.optional(types.array(Notification), []),
  newNotification: types.optional(NewNotification, {
    appearanceType: 'banner',
    type: 'success',
    title: NOTIFICATION_DEFAULT.title,
    body: NOTIFICATION_DEFAULT.body,
    button: 'hidden',
    buttonTitle: NOTIFICATION_DEFAULT.buttonTitle,
    buttonLink: NOTIFICATION_DEFAULT.buttonLink,
    dates: 'start',
    startDateOnly: formatISO(moment().toDate()).split('T')[0],
    endDateOnly: formatISO(moment().add(2, 'days').toDate()).split('T')[0],
    termsOfConditions: false,
    privacyPolicy: false,
    userAcknowledgement: false
  }),
  currentTos: types.optional(types.maybeNull(TosNotification), null)
}).views(store => ({
  get notificationList() {
    return toJS(store.notificationListValue);
  }
})).actions(store => ({
  setCurrentToS(data) {
    try {
      store.currentTos = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  resetCurrentToS() {
    try {
      store.currentTos = null;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  changeNewNotification(param, value) {
    try {
      store.newNotification[param] = value;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  resetNewNotification() {
    try {
      store.newNotification = {
        appearanceType: 'banner',
        type: 'success',
        title: NOTIFICATION_DEFAULT.title,
        body: NOTIFICATION_DEFAULT.body,
        button: 'hidden',
        buttonTitle: NOTIFICATION_DEFAULT.buttonTitle,
        buttonLink: NOTIFICATION_DEFAULT.buttonLink,
        dates: 'start',
        startDateOnly: formatISO(moment().toDate()).split('T')[0],
        endDateOnly: formatISO(moment().add(2, 'days').toDate()).split('T')[0],
        termsOfConditions: false,
        privacyPolicy: false,
        userAcknowledgement: false
      };
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setNewNotification(data) {
    try {
      store.newNotification = {
        appearanceType: data.appearanceType.toLocaleLowerCase(),
        type: data.notificationType.toLocaleLowerCase(),
        title: data.title,
        body: data.body,
        button: data.showButton ? 'visible' : 'hidden',
        buttonTitle: data.buttonTitle,
        buttonLink: data.buttonUrl,
        dates: !!data.endDateOnly ? 'start-end' : 'start',
        startDateOnly: data.startDateOnly,
        endDateOnly: data.endDateOnly,
        termsOfConditions: data.termsOfConditions ? true : false,
        privacyPolicy: data.privacyPolicy ? true : false,
        userAcknowledgement: data.userAcknowledgement ? true : false
      };
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setNotificationList(data) {
    try {
      store.notificationListValue = cast([]);
      store.notificationListValue = cast(data);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  }
}));
export const notifications = types.optional(NotificationsStore, {});